import { useMemo } from 'react';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import {
  MigrationBrokenList,
  MigrationChallenge,
  MigrationKeyType,
  MigrationOverview,
  MigrationOriginTestResult,
  MigrationRun,
  MigrationRunOptions,
  MigrationScanResult,
  MigrationVerifyMethod,
  MigrationVerifyResult,
} from 'src/types/TMigration';
import {
  addMigrationKey,
  deleteMigrationSetup,
  getMigrationBrokenAssets,
  cancelMigrationCopy,
  challengeMigrationKey,
  KeyPayload,
  removeMigrationKey,
  scanMigrationUrls,
  SetOriginPayload,
  SetProviderPayload,
  setMigrationOrigin,
  setMigrationProvider,
  startMigrationCopy,
  testMigrationOrigin,
  verifyMigrationKey,
} from '../functions/migrationApi';
import { queryFnFetchErrorWrapper } from '../utils/errorHandlers';
import { queryNames } from '../utils/queryNames';

/**
 * Every write here answers with the full overview, so the page re-renders from
 * one authoritative document rather than patching its own state and drifting
 * from what the server actually stored. `useInvalidateOverview` is only for the
 * calls that do NOT return it.
 */
function useInvalidateOverview() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries([queryNames.migrationOverview]);
}

export function useSetMigrationProviderMutation(
  mutationOptions?: UseMutationOptions<MigrationOverview, FetchError, SetProviderPayload>,
) {
  const accessToken = useAccessToken();
  const invalidate = useInvalidateOverview();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((data: SetProviderPayload) => setMigrationProvider(data, accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await invalidate();
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

/**
 * Clear the set-up so a different provider can be chosen.
 *
 * An account migrates from one provider at a time, so this is the only route to
 * changing vendors. It removes the configuration and leaves every copied image
 * exactly where it is.
 */
export function useDeleteMigrationSetupMutation(
  mutationOptions?: UseMutationOptions<MigrationOverview, FetchError, void>,
) {
  const accessToken = useAccessToken();
  const invalidate = useInvalidateOverview();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper(() => deleteMigrationSetup(accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await invalidate();
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useAddMigrationKeyMutation(
  mutationOptions?: UseMutationOptions<MigrationOverview, FetchError, KeyPayload>,
) {
  const accessToken = useAccessToken();
  const invalidate = useInvalidateOverview();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((data: KeyPayload) => addMigrationKey(data, accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await invalidate();
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useRemoveMigrationKeyMutation(
  mutationOptions?: UseMutationOptions<MigrationOverview, FetchError, KeyPayload>,
) {
  const accessToken = useAccessToken();
  const invalidate = useInvalidateOverview();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((data: KeyPayload) => removeMigrationKey(data, accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await invalidate();
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

/** Issue the nonce the customer must publish. Reads nothing; changes no state
 *  the overview shows, so it does not invalidate it. */
export function useChallengeMigrationKeyMutation(
  mutationOptions?: UseMutationOptions<MigrationChallenge, FetchError, KeyPayload>,
) {
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((data: KeyPayload) => challengeMigrationKey(data, accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, mutationOptions);
}

/**
 * Check a proof of ownership.
 *
 * A failed check answers 200 with `verified: false` and a reason, because "we
 * looked and it did not check out" is a normal outcome the screen has to render
 * — not an error to throw the user out to a generic failure state.
 */
export function useVerifyMigrationKeyMutation(
  mutationOptions?: UseMutationOptions<
    MigrationVerifyResult,
    FetchError,
    KeyPayload & { method: MigrationVerifyMethod }
  >,
) {
  const accessToken = useAccessToken();
  const invalidate = useInvalidateOverview();

  const mutationFn = useMemo(
    () =>
      queryFnFetchErrorWrapper((data: KeyPayload & { method: MigrationVerifyMethod }) =>
        verifyMigrationKey(data, accessToken),
      ),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      if (result?.verified) await invalidate();
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useSetMigrationOriginMutation(
  mutationOptions?: UseMutationOptions<MigrationOverview, FetchError, SetOriginPayload>,
) {
  const accessToken = useAccessToken();
  const invalidate = useInvalidateOverview();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((data: SetOriginPayload) => setMigrationOrigin(data, accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await invalidate();
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export type TestOriginPayload =
  | { api_key?: string; api_secret?: string; base_url?: string }
  | undefined;

export function useTestMigrationOriginMutation(
  mutationOptions?: UseMutationOptions<MigrationOriginTestResult, FetchError, TestOriginPayload>,
) {
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((creds: TestOriginPayload) => testMigrationOrigin(accessToken, creds)),
    [accessToken],
  );

  return useMutation(mutationFn, mutationOptions);
}

/**
 * Fetch every unusable image, for the download.
 *
 * A mutation rather than a query because it is an action the customer takes,
 * once: nothing should fetch a ten-thousand-row list on page load.
 */
export function useMigrationBrokenAssetsMutation(
  mutationOptions?: UseMutationOptions<MigrationBrokenList, FetchError, void>,
) {
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getMigrationBrokenAssets(accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, mutationOptions);
}

/** The compatibility report. Creates nothing and contacts nothing — it is the
 *  URL translator run over a list, so it is safe to run as often as wanted. */
export function useScanMigrationUrlsMutation(
  mutationOptions?: UseMutationOptions<MigrationScanResult, FetchError, string>,
) {
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((urls: string) => scanMigrationUrls(urls, accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, mutationOptions);
}

export function useStartMigrationCopyMutation(
  mutationOptions?: UseMutationOptions<MigrationRun, FetchError, MigrationRunOptions>,
) {
  const accessToken = useAccessToken();
  const invalidate = useInvalidateOverview();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((options: MigrationRunOptions) => startMigrationCopy(options, accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await invalidate();
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useCancelMigrationCopyMutation(
  mutationOptions?: UseMutationOptions<{ cancelling: number }, FetchError, number>,
) {
  const accessToken = useAccessToken();
  const invalidate = useInvalidateOverview();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((runId: number) => cancelMigrationCopy(runId, accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await invalidate();
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}
